import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from "react-router-dom";
import Grid from '@mui/material/Grid2';

import { 
  Character,
} from "@dndModels";
import { User } from "@models";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import EditableLabel from "@components/input/strings/EditableLabel";

import CharacterMainDetails from "@dndComponents/character/CharacterMainDetails";
import LoadingBox from "@components/input/LoadingBox";

import CharacterBackgroundButton from "@dndComponents/character/background/CharacterBackgroundButton";
import CharacterRaceButton from "@dndComponents/character/race/CharacterRaceButton";
import CharacterClassButton from "@dndComponents/character/classes/CharacterClassButton";
// import CharacterHP from "./misc/CharacterHP";
import CharacterRestButton from "@dndComponents/character/misc/CharacterRestButton";
import CharacterInspirationButton from "@dndComponents/character/misc/CharacterInspirationButton";
import CharacterGiftButton from "@dndComponents/character/gifts/CharacterGiftButton";
import CharacterImageButton from "@dndComponents/character/misc/CharacterImageButton";
import CharacterSpeedButton from "@dndComponents/character/misc/CharacterSpeedButton";
import CharacterACButton from "@dndComponents/character/misc/CharacterACButton";
import CharacterTransformButton from "@dndComponents/character/transform/CharacterTransformButton";
import CharacterSizeButton from "@dndComponents/character/misc/CharacterSizeButton";
import CharacterKnowledgeButton from "@dndComponents/character/knowledge/CharacterKnowledgeButton";
import CustomRolls from "@dndComponents/character/CustomRolls";
import CharacterInitiative from "@dndComponents/character/misc/CharacterInitiative";
import HPInput from "@dndComponents/other/HPInput";
import CharacterAdvantageButton from "@dndComponents/character/misc/CharacterAdvantageButton";
import CharacterBastionButton from "@dndComponents/character/bastions/CharacterBastionButton";
import CharacterMailButton from "@dndComponents/character/receivables/CharacterMailButton";
import CharacterLevelDisplay from "@dndComponents/character/misc/CharacterLevelDisplay";
import CharacterProficiencyBonus from "@dndComponents/character/misc/CharacterProficiencyBonus";
import CharacterNotesButton from './misc/CharacterNotesButton';
import CharacterSettings from './customizations/CharacterSettings';
import FlexBox from '@components/layout/FlexBox';

interface Props { 
  char: Character;
  shared?: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterSheet({ 
  char,
  shared = false,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [loading_detail, setLoadingDetail] = useState("");
  const [owner, setOwner] = useState<User | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const recalculate = useCallback(() => {
    console.log("recalculate");
    setTimeout(() => {
      char_util.buildMind(char);
    }, 1000);
  }, [char]);

  const spellsChanged = useCallback( async () => {
    setTimeout( async () => {
      console.log("spellsChanged");
      await char_util.doRecalcs2({char});
      char_util.recalcSpells(char);
    }, 1000);
  }, [char]);

  const transformed = useCallback(() => {
    console.log("transformed");
    console.log(char.spirit.transform);
    setReloading(true);
  }, [char]);
  
  const load = useCallback( async () => {
    // This is necessary for minions already on the char to calculate properly.
    // if (char.spirit.minions.some(o => o instanceof ComplexCreatureInstance)) {
    //   await char_util.buildMind(char);
    // }
    setLoading(false);
  }, [char]);

  // const subscribed = useRef(false);

  useEffect(() => {
    // if (subscribed.current) return;
    // subscribed.current = true;
  
    char.subscription_holder.subscribe("Recalculate", mounted, recalculate, `CharacterSheet_${char._id}`);
    char.subscription_holder.subscribe("Transform", mounted, transformed);
    char.subscription_holder.subscribe("Spells", mounted, spellsChanged);
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [char, load, recalculate, spellsChanged, transformed, fetched]);

  const renderIconButtons = () => {
    return (
      <FlexBox direction="column">
        <FlexBox direction="row">
          <FlexBox className="padded">
            <CharacterSettings
              char={char}
            />
          </FlexBox>
          <FlexBox className="padded">
            <CharacterRestButton 
              char={char}
            />
          </FlexBox>
          <FlexBox className="padded">
            <CharacterKnowledgeButton 
              char={char}
            />
          </FlexBox>
          <FlexBox className="padded">
            <CharacterGiftButton 
              character={char}
              disabled={shared}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox>
          <div style={{ float: "right" }}>
            <FlexBox direction="row">
              { char.c_options.use_bastions && 
                <FlexBox className="padded">
                  <CharacterBastionButton 
                    character={char}
                    disabled={shared}
                  />
                </FlexBox>
              }
              <FlexBox className="padded">
                <CharacterTransformButton
                  char={char}
                />
              </FlexBox>
              { char.campaign &&
                <FlexBox className="padded">
                  <CharacterMailButton 
                    char={char}
                    disabled={shared}
                  />
                </FlexBox>
              }
              <FlexBox className="padded">
                <CharacterNotesButton 
                  char={char}
                />
              </FlexBox>
            </FlexBox>
          </div>
        </FlexBox>
      </FlexBox>
    );
  }

  const renderBar1 = () => {
    if (reloading) {
      return (<LoadingBox />);
    } else {
      return (
        <Grid size={12} className="bg_black_transp padded">
          <Grid container spacing={0} direction="row">
            <Grid size={4}>
              <div style={{ float: "left" }}>
                <CharacterImageButton 
                  character={char}
                  disabled={shared}
                />
              </div>
              <div style={{ float: "left" }}>
                <Grid container spacing={0} direction="row">
                  <Grid size={12} style={{ marginLeft: "4px" }}>
                    <EditableLabel 
                      name="Name"
                      value={char.name}
                      fontSize={24}
                      disabled={shared}
                      flash_on_empty
                      onChange={(changed: string) => {
                        const new_obj = char;
                        new_obj.name = changed;
                        new_obj.changed();
                      }}
                    />
                  </Grid>
                  <Grid size={12} style={{ marginLeft: "4px" }}>
                    <CharacterLevelDisplay character={char} />
                  </Grid>
                  { owner && 
                    <Grid size={12} style={{ fontSize: "8px", color: "gray", marginLeft: "4px" }}>
                      { owner.profile_name }
                    </Grid>
                  }
                  <Grid size={12} style={{ marginLeft: "4px" }}>
                    <CharacterTransformButton
                      char={char}
                      show_name
                    />
                  </Grid>
                </Grid>
              </div>
            </Grid>
            <Grid size={5}
              container spacing={0} direction="row">
              <Grid size={12}>
                <CharacterClassButton 
                  char={char}
                  disabled={shared}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CharacterRaceButton
                  char={char}
                  disabled={shared}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                { !char.spirit.sidekick &&
                  <CharacterBackgroundButton
                    character={char}
                    disabled={shared}
                  />
                }
              </Grid>
            </Grid>
            <Grid size={3}
              container spacing={0} direction="row">
              <Grid size={12}>
                <div style={{ float: "right", marginRight: "4px", maxWidth: "245px" }}>
                  <Grid container spacing={0} direction="row">
                    <Grid size={12}>
                      <HPInput 
                        char={char.char_interface}
                      />
                    </Grid>
                    <Grid size={12}>
                      <CharacterInspirationButton 
                        char={char}
                      />
                    </Grid>
                    <Grid size={12}>
                      <CharacterAdvantageButton 
                        char={char}
                      />
                    </Grid>
                  </Grid>
                </div>
                <div style={{ float: "right", marginRight: "4px", maxWidth: "125px" }}>
                  { renderIconButtons() }
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }
  }

  const renderBar2 = () => {
    // console.log("renderBar2");
    if (reloading) {
      return (<LoadingBox />);
    } else {
      return (
        <>
          <Grid
            style={{ borderTop: "1px solid" }}
            className="bg_black_transp"
            size={{
              xs: 3
            }}>
            <CharacterSizeButton
              char={char}
            />
          </Grid>
          <Grid size={9} style={{ borderTop: "1px solid" }} className="bg_black_transp">
            <Grid container spacing={0} direction="row">
              <Grid size={3}>
                <CharacterProficiencyBonus
                  char={char}
                />
              </Grid>
              <Grid size={3}>
                <CharacterSpeedButton
                  char={char}
                />
              </Grid>
              <Grid size={3}>
                <CharacterInitiative
                  char={char.char_interface}
                />
              </Grid>
              <Grid size={3}>
                <CharacterACButton
                  char={char}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      );
    }
  }

  if (!fetched || loading) {
    return (<LoadingBox message={loading_detail} size={window.innerHeight * 0.5} height={window.innerHeight - 81} />);
  } else if (api.user === null && !shared) {
    return <Navigate to="/" />;
  } else { 
    // console.log("Char Sheet");
    return (
      <Grid container spacing={0} direction="row" className="dracoLich" style={{ 
        lineHeight: "1.5", 
        backgroundSize: "cover", // `${window.innerWidth}px`, 
        backgroundAttachment: "fixed", 
        backgroundImage: (char.bg_image_url === "" ? undefined : `url(${char.bg_image_url})`),
      }}>
        <Grid size={12}>
          <Grid container spacing={0} direction="row">
            { renderBar1() }
            { renderBar2() }
          </Grid>
        </Grid>
        <Grid size={12}>
          <CharacterMainDetails 
            char={char}
            // campaign={campaign}
            shared={shared}
          />
        </Grid>
        <CustomRolls
          char_interface={char.char_interface}
        />
      </Grid>
    ); 
  }
}
